// `CandidateContainer.getCandidateName` (candidate-container.resolver.ng1.ts:58) and
// `trimCandidateName` / `formatLastMessageDate` on the slim list's component.
//
// ⚠️ MOST CANDIDATES IN A REAL LIST HAVE NO NAME. Nothing in the DTO guarantees one — a
// candidate sourced from an ATS export, or one whose profile is not shared, arrives with
// `name` empty — and the row is the only place the fallback is applied. Rendering
// `candidate.name` directly, which is the obvious thing to do, leaves five of the eight rows
// in the reference capture with a blank line where production shows "Candidate 22719".

/**
 * `CandidateContainer.DEFAULT_CANDIDATE_NAME` and
 * `ClientContactContainer.DEFAULT_CLIENT_CONTACT_NAME`.
 *
 * ⚠️ THE TWO PRODUCTS DIFFER ONLY IN THIS WORD. Everything else — the id fallback, the
 * ten-character elision, the 50-character trim — is duplicated verbatim across the two
 * resolvers upstream, so the word is the parameter and the rest is shared.
 */
const DEFAULT_CANDIDATE_NAME = 'Candidate'
const DEFAULT_CLIENT_CONTACT_NAME = 'Contact'

/** `trimCandidateName` truncates to this; the `title` attribute keeps the full string. */
const MAX_NAME_LENGTH = 50

/** The entity fields the name is derived from — candidates and client contacts share them. */
export interface NamedEntity {
  name?: string | null
  externalId?: string | null
}

/**
 * ⚠️ THE EXTERNAL ID IS ELIDED AT 10 CHARACTERS, NOT AT `MAX_NAME_LENGTH`, and the ellipsis is
 * three literal dots inside the string rather than a CSS truncation. A Bullhorn id fits; a
 * composite id from a smaller ATS does not, and upstream's cutoff is what decides where.
 */
function entityName(entity: NamedEntity, fallback: string): string {
  if (entity.name !== undefined && entity.name !== null && entity.name !== '') return entity.name

  const externalId = entity.externalId
  if (externalId === undefined || externalId === null || externalId === '') return fallback

  return `${fallback} ${externalId.length > 10 ? `${externalId.slice(0, 10)}...` : externalId}`
}

/** `CandidateContainer.getCandidateName` — the full name, as the row's `title`. */
export function candidateName(entity: NamedEntity): string {
  return entityName(entity, DEFAULT_CANDIDATE_NAME)
}

/** `ClientContactContainer.getClientContactName` — the same rule, a different noun. */
export function clientContactName(entity: NamedEntity): string {
  return entityName(entity, DEFAULT_CLIENT_CONTACT_NAME)
}

/** `trimCandidateName` — what the row DISPLAYS, against `candidateName` in the tooltip. */
export function trimCandidateName(entity: NamedEntity): string {
  return candidateName(entity).slice(0, MAX_NAME_LENGTH)
}

/** `trimClientContactName` — same cap; both components declare their own `MAX_NAME_LENGTH: 50`. */
export function trimClientContactName(entity: NamedEntity): string {
  return clientContactName(entity).slice(0, MAX_NAME_LENGTH)
}

/**
 * `formatLastMessageDate` — the text beside the envelope.
 *
 * Upstream formats through `Intl.DateTimeFormat()` with no locale argument, so it follows the
 * BROWSER's locale rather than the tenant's. Passing a locale here would be a change, not a
 * port: the capture environment and a user's machine would then disagree with production.
 */
export function lastMessageDisplay(lastReceivedMessage?: number | null): string {
  if (lastReceivedMessage === undefined || lastReceivedMessage === null || lastReceivedMessage === 0) return 'Never'
  return new Intl.DateTimeFormat().format(new Date(lastReceivedMessage))
}
